// Fuente unica de las imagenes de muestra de cada libro.
//
// Antes esta lista vivia duplicada a mano en tres componentes (LibroContent,
// home-pack-detail y home-libro2-fisicos) y se desincronizaron: la pagina del
// libro mostraba solo la tapa en varios idiomas mientras las otras dos si
// mostraban las paginas, y un slot quedo apuntando a un TI_back.jpg que nunca
// existio (miles de 404 en el log). Todo eso sale de aca ahora.
//
// Los archivos siguen un patron fijo en public/:
//   tapa:    /book/<idioma>/<archivo de tapa>
//   paginas: /book/<idioma>/<CODIGO>/<CODIGO>_pagN.jpg   (N desde 1)
//   contra:  /book/<idioma>/<CODIGO>/<CODIGO>_back.jpg   (solo donde exista)
//
// Por eso las paginas se declaran como CANTIDAD y no como lista: asi las
// asimetrias reales quedan a la vista (ej: TI tiene 4 paginas salvo en ingles,
// que tiene 3) en vez de esconderse entre 49 arrays escritos a mano.
//
// `lib/books/__tests__/gallery.test.js` recorre todo lo que genera este modulo
// y falla si algun archivo no esta en public/, para que no vuelva a pasar.

export const BOOK_LANGS = ["es", "en", "pt", "fr", "it", "de", "ja", "tr"];

const DEFAULT_LANG = "es";

const BOOKS = {
  AI: {
    cover: "tapa_armonia_ilustrada.jpg",
    pages: { default: 2 },
    backIn: ["es"],
  },
  MI: {
    cover: "tapa_modos_ilustrados.jpg",
    pages: { default: 2 },
    backIn: ["es"],
  },
  AI2: {
    cover: "tapa_armonia_ilustrada_2.jpg",
    pages: { default: 2 },
    backIn: ["es"],
  },
  TI: {
    cover: "tapa_tensiones_ilustradas.jpg",
    pages: { default: 4, en: 3 },
    backIn: [],
  },
  GM: {
    cover: "tapa_glue_magico_para_guitarristas.jpg",
    pages: { default: 4 },
    backIn: [],
  },
  AR: {
    cover: "tapa_arreglos_ilustrados.jpg",
    pages: { default: 5 },
    backIn: [],
  },
  EI: {
    // Las 5 paginas las eligio Brian (22-sep-2026): 3, 16, 46, 53 y 106 del PDF,
    // en ese orden. Los 7 PDFs tienen 154 paginas y la misma paginacion, asi que
    // en todos los idiomas son las mismas paginas del libro, cada una en su
    // idioma. Se renderizan con ai-helper/pdf_to_book_images.py --book EI
    // --pages 3,16,46,53,106 (los PDFs estan en el bucket "books" de R2).
    cover: "tapa_escalas_ilustradas.png",
    pages: { default: 5 },
    backIn: [],
  },
  MIC: {
    // Tapa definitiva de Brian, en los 7 idiomas. Son los PNG del Drive tal
    // cual ("Tapas y Banners/08 - Musica de cero/Cover Digital - 3508 x 2480 -
    // <ESP|ENG|POR|FRA|ITA|ALE|JPN>.png"), copiados sin reencodear, igual que
    // los de Escalas. La PROVISORIA que habia antes y el script que la generaba
    // ya no existen.
    cover: "tapa_musica_ilustrada_de_cero-v2.png",
    // En preventa: todavia no hay paginas de muestra renderizadas.
    pages: { default: 0 },
    backIn: [],
  },
};

export const BOOK_CODES = Object.keys(BOOKS);

function normalizeLang(lang) {
  const l = String(lang || "").toLowerCase().split("-")[0];
  return BOOK_LANGS.includes(l) ? l : DEFAULT_LANG;
}

/** Ruta de la tapa del libro en ese idioma. */
export function bookCover(code, lang) {
  const book = BOOKS[code];
  if (!book) return "";
  // Un libro puede existir y todavia no tener tapa (MIC). Sin esta guarda el
  // template armaba "/book/es/null", que es truthy: bookGallery devolvia un
  // elemento y la ficha dibujaba una imagen rota en vez de no dibujar nada.
  if (!book.cover) return "";
  return `/book/${normalizeLang(lang)}/${book.cover}`;
}

/** Solo las paginas interiores (sin la tapa). */
export function bookPages(code, lang) {
  const book = BOOKS[code];
  if (!book) return [];

  const l = normalizeLang(lang);
  const count = book.pages[l] ?? book.pages.default ?? 0;
  const base = `/book/${l}/${code}`;

  const out = [];
  for (let n = 1; n <= count; n += 1) {
    out.push(`${base}/${code}_pag${n}.jpg`);
  }
  if (book.backIn.includes(l)) {
    out.push(`${base}/${code}_back.jpg`);
  }
  return out;
}

/** Galeria completa: tapa primero y despues las paginas. */
export function bookGallery(code, lang) {
  const cover = bookCover(code, lang);
  return cover ? [cover, ...bookPages(code, lang)] : [];
}

/**
 * Cuantas paginas del interior se muestran gratis en la ficha de ese libro y ese
 * idioma. NO cuenta la tapa ni la contratapa: es el numero que la meta
 * description le promete al que busca "pdf gratis", asi que tiene que ser
 * exactamente lo que va a ver. Da 0 donde todavia no hay muestra (Musica de Cero).
 */
export function bookPageCount(code, lang) {
  const book = BOOKS[code];
  if (!book) return 0;
  const l = normalizeLang(lang);
  return book.pages[l] ?? book.pages.default ?? 0;
}
